import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignOut } from '@fortawesome/pro-light-svg-icons'

import { generateRedirectUrl } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { generateUrls } from '@hooks/useProviderAccount'
import useAgency from '@hooks/useAgency'
import useCampaign from '@hooks/useCampaign'
import useOrder, { useOrderForm } from '@hooks/useOrder'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EditOrderProcessing from './components/EditOrderProcessing'
import EditOrderStatus from './components/EditOrderStatus'
import OrderItems from './components/OrderItems'

const requestOptions = {
  include_agency_provider_account_for_provider: true,
  include_order_agency: true,
  include_order_campaign: true,
  include_order_item_artwork: true,
  include_order_items: true,
}

const MODAL_KEY = 'EditOrderModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateOrder: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditOrderModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateOrder } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { order: initOrder } = modalPayload

  const {
    callbacks: useOrderCallbacks,
    callbacks: {
      bulkStatusUpdate: bulkStatusUpdateFn,
      updateOrder: updateFn,
    },
    order,
    updating,
  } = useOrder(initOrder, { performHttpRequests: true, requestOptions })

  const useOrderFormPayload = useOrderForm(order, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      agency_id,
      campaign_id,
      shipping_address,
    },
    handlers,
  } = useOrderFormPayload

  const agencyPayload = useAgency({ id: agency_id })
  const {
    agency: { title: agencyTitle },
  } = agencyPayload

  const { editProviderAccountUrl } = generateUrls({ agency_id })

  const campaignPayload = useCampaign({ id: campaign_id })
  const {
    campaign: { id: campaignId, title: campaignTitle },
    urls: {
      editCampaignUrl,
    },
  } = campaignPayload

  const updateOrderPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Order Updated',
  }

  const payload = encodeURIComponent(JSON.stringify({ order: { id: order?.id } }))

  const editAgencyUrlWithRedirect = `${editProviderAccountUrl}?redirect=${generateRedirectUrl({
    append: `showModal=${MODAL_KEY}&showModalPayload=${payload}`,
  })}`

  const editCampaignUrlWithRedirect = `${editCampaignUrl}?redirect=${generateRedirectUrl({
    append: `showModal=${MODAL_KEY}&showModalPayload=${payload}`,
  })}`

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Edit" titleSecondLine="Order" />

      <SidebarModal.Content>
        <Form.Field label="Agency">
          <SidebarModal.ListItem boxProps={{ paddingY: 'small' }} title={agencyTitle}>
            {/*
             // @ts-expect-error TS2339 */}
            <SidebarModal.ListItem.Actions>
              <Button
                // @ts-expect-error TS2322
                as="a"
                buttonStyle="ghostEdit"
                icon={<FontAwesomeIcon icon={faSignOut} />}
                iconPosition="right"
                href={editAgencyUrlWithRedirect}
                size="small"
              >
                View
              </Button>
              {/*
             // @ts-expect-error TS2339 */}
            </SidebarModal.ListItem.Actions>
          </SidebarModal.ListItem>
        </Form.Field>

        {!!campaignId && (
          <Form.Field label="Campaign" marginTop="large">
            <SidebarModal.ListItem boxProps={{ paddingY: 'small' }} title={campaignTitle}>
              {/*
               // @ts-expect-error TS2339 */}
              <SidebarModal.ListItem.Actions>
                <Button
                  // @ts-expect-error TS2322
                  as="a"
                  buttonStyle="ghostEdit"
                  icon={<FontAwesomeIcon icon={faSignOut} />}
                  iconPosition="right"
                  href={editCampaignUrlWithRedirect}
                  size="small"
                >
                  View
                </Button>
                {/*
               // @ts-expect-error TS2339 */}
              </SidebarModal.ListItem.Actions>
            </SidebarModal.ListItem>
          </Form.Field>
        )}

        <Form.Field label="Delivery" marginTop="large">
          {/*
           // @ts-expect-error TS2322 */}
          <textarea
            name="shipping_address"
            style={{ minHeight: 100, resize: 'vertical' }}
            value={shipping_address}
            {...handlers}
          />
        </Form.Field>

        <OrderItems orderId={order.id} />

        <EditOrderStatus entityState={entityState} bulkStatusUpdateFn={bulkStatusUpdateFn} />

        <EditOrderProcessing
          // @ts-expect-error TS2345
          callbacks={useOrderCallbacks}
          order={order}
        />

      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateOrder(updateOrderPayload)}
          size="large"
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditOrderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
